import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';

// MUI
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery } from "@mui/material";

// OpenLayers
import Geometry from "ol/geom/Geometry";
import proj4 from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection } from "ol/proj";
import OlSourceVector from "ol/source/Vector";
import MapEvent from "ol/MapEvent";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlFeature from "ol/Feature";
import OlLayerVector from "ol/layer/Vector";
import OlLayerGroup from "ol/layer/Group";
import OlCollection from "ol/Collection";
import OlBaseLayer from "ol/layer/Base";
import OlPoint from "ol/geom/Point";
import OlLineString from "ol/geom/LineString";
import OlMultiLineString from "ol/geom/MultiLineString";
import WKT from "ol/format/WKT";
import { Coordinate } from "ol/coordinate";

// Custom components
import Map from "@/components/Map/Map";
import { Controls, CoordZoomStatusControl, RotateControl, ZoomControl, GeoLocateControl, RadiusRulerControl } from "@/components/Map/Controls";
import { Layers, VectorLayer } from "@/components/Map/Layers";
import { Overlays, PopupOverlay } from "@/components/Map/Overlays";
import { Interactions, DefaultInteractions } from "@/components/Map/Interactions";
import { selectedStyle, poiStyle, highlightedPoiStyle, ktStyle, selectedSegmentStyle } from "@/components/Map/mapStyles";
import UserContext from "@/components/UserContext/UserContext";
import DialogContext from "@/ui/DialogContext/DialogContext";
import { flattenLayers } from "@/lib/olHelpers";

import GeoAPILayers from "@/components/Map/Layers/GEO/GeoAPILayers";
import ClientLayer from "@/components/Map/Layers/ClientLayer";
import PointQuickActions from "./PointQuickActions";

import dataController from "@/lib/dataController";

import PublicNavBar from "@/ui/PublicPage/PublicNavBar";
import modelPOI from "@/models/poi";
import modelStaze from "@/models/staze";
import modelObilaznice from "@/models/obilaznice";
import modelLDT from "@/models/ldt";

import useRouting, { RoutingProps } from "@/components/MapRouting/useRouting";

import { PublicInfoPane, PublicOptionsPane, PublicRoutesPane, PublicSearchPane, PublicCommentsPane, ObilaznicePane, LDTPane } from "@/ui/PublicPage/SidebarPanes";
import { sifrarnici } from "@/lib/sifrarnici";
import api from "@/lib/api";
import {
  areWarningsIdentical,
  getStoredRouteWarnings,
  parseRouteWarnings,
  shuffleWarnings,
  storeRouteWarnings,
} from "@/lib/routeWarnings";

import PublicWarningDialog from "./PublicWarningDialog";

import GeoAPIBaseLayers from "@/components/Map/Layers/GEO/GeoAPIBaseLayers";

// Services
import mapService from "@/services/mapService";
import gsService from "@/services/gsService";

// Types
import { DCRecord } from "@/@types/lib/dataController";
import { BoolSettings, ViewOptionsType, IMapView } from "@/@types/common/index";
import {
  IMapProps,
  CardPosition,
  IInfoPane,
  IInfoPaneData,
  InfoPaneType,
  IInfoPaneChip
} from "@/@types/views/GIS/map";
import { GFIResponseType } from "@/@types/services/gsService";

function MainMap(props: IMapProps) {
  const userContext = useContext(UserContext);
  const dialogContext = useContext(DialogContext);
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));

  const MAP_ID = 1;

  const navigate = useNavigate();

  type ShareResponseData = {
    config: string;
    object_id?: number;
    object_type?: string;
    object_uuid?: string;
  };

  const [mapInitialized, setMapInitialized] = useState(false);
  // localStorage map state
  const mapStateJSON = localStorage.getItem( 'public_map_state' );

  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center:
      mapStateJSON && mapStateJSON !== null &&
        mapStateJSON !== undefined && JSON.parse(mapStateJSON).center &&
        JSON.parse(mapStateJSON).center !== null
        ? JSON.parse(mapStateJSON).center
        : [1731757, 5581737],
    zoom:
      mapStateJSON && mapStateJSON !== null &&
        mapStateJSON !== undefined && JSON.parse(mapStateJSON).zoom &&
        JSON.parse(mapStateJSON).zoom !== null
        ? JSON.parse(mapStateJSON).zoom
        : 8,
    // extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    extent: [1447153, 5104226, 2162937, 5867310],
    constrainOnlyCenter: true,
    // projection: wgs84PM,
    minZoom: 8,
    maxZoom: 18,
    constrainResolution: true,
  });

  const [zoomToExtent, setZoomToExtent] = useState(undefined);

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [baseLayersCollection, setBaseLayersCollection] = useState<Array<OlBaseLayer> | undefined>(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);

  const [infoPane, setInfoPane] = useState<IInfoPane | undefined>(undefined);
  const [commentPane, setCommentPane] = useState<any | undefined>(undefined);
  const [obilaznicePane, setObilaznicePane] = useState<any | undefined>(undefined);
  const [ldtPane, setLdtPane] = useState<any | undefined>(undefined);

  const [defaultExtent, setDefaultExtent] = useState(
    userContext?.mapSettings?.default_extent
      ? userContext.mapSettings.default_extent
      : [1437016, 5271097, 2206278, 5860579]
  );

  const highlightedPOI = useRef<OlFeature<Geometry> | null>(null);
  const [highlightedRoutePosition, setHighlightedRoutePosition] = useState<number[]>([]);
  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [selectedSegmentSource, setSelectedSegmentSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [poiRecords, setPoiRecords] = useState<DCRecord[]>([]);
  const [poiSources, setPoiSources] = useState<Array<OlSourceVector<OlFeature<Geometry>>>>();

  const [clientLayersCollection, setClientLayersCollection] = useState<OlCollection<OlBaseLayer>>(new OlCollection)

  const selectedCoords = useRef<Array<number>>([])

  const [drawerStage, setDrawerStage] = useState<number>(0)
  const [pane, setPane] = useState<number | undefined>(undefined)
  const [open, setOpen] = useState<boolean>(false)

  const geoLocateElementRef = useRef<HTMLLIElement>(null);
  const radiusRulerElementRef = useRef<HTMLLIElement>(null);

  const clearSelectedSource = useCallback(() => {
    setSelectedSource(new OlSourceVector({}))
  }, []);

  const clearHighlihtedRoutePosition = useCallback(() => {
    setHighlightedRoutePosition([]);
  }, []);

  const handleClearRoute = useCallback(() => {
    clearSelectedSource();
    clearHighlihtedRoutePosition();
  }, []);

  const { loadRoutingPoints, addToRoute } = useRouting({ onClearRoute: handleClearRoute } as RoutingProps);

  // define proj
  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs(
    "EPSG:31276",
    "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  const htrs96 = OlGetProjection("EPSG:3765");
  const wgs84 = OlGetProjection("EPSG:4326");
  const wgs84PM = OlGetProjection("EPSG:3857");

  // defaultViewCenter = [1731757, 5581737];
  // initialDefaultExtent = [1688674,5501166,1688774,5501266];
  const defaultViewCenter = userContext?.mapSettings?.initial_view_center;
  const initialDefaultExtent = userContext?.mapSettings?.default_extent;

  const dcPOI = new dataController(modelPOI, "public/poi");
  const dcStaze = new dataController(modelStaze, "public/staze");
  const dcObilaznice = new dataController(modelObilaznice, "public/obilaznice");
  const dcLDT = new dataController(modelLDT, "public/ldt");

  useEffect(() => {
    getLayers();
    refreshPOI();
    checkForShare();
    checkForComment();
  }, []);

  const getLayers = () => {

    mapService.getLayers(MAP_ID).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const createPOIFeatures = (records: DCRecord[]) => {
    return records.map(x => {
      //@ts-ignore
      const f = x.geom as OlFeature<Geometry>
      f.setId(x.id as number);
      f.set("poi_tip_id", x.poi_tip_id);
      f.set("naziv", x.naziv);
      f.set("ttoken", x.ttoken);
      return f;
    });
  };

  const createPOILayerVector = (id: string, token: string, source: OlSourceVector) => {
    const layer = new OlLayerVector({
      source: source,
      visible: true
    });
    layer.set("id", id);
    layer.set("title", token);
    layer.set("icon", 'images/icons/dom_1.svg')
    return layer;
  }

  const refreshPOI = () => {
    dcPOI.GetData().then((resp) => {
      if (resp.success) {
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setPoiRecords(resp.data as DCRecord[]);
          const featuresPlaninarski = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && (x.poi_tip_id === 31 || x.poi_tip_id === 32)));
          const featuresSkloniste = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 33));
          const featuresPenjaliste = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 40));
          const featuresOpasnost = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 50));
          const featuresVrh = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 60));
          const featuresOdmoriste = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 91));
          const featuresPolazista = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 90));
          const featuresPrir = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id as number >= 70 && x.poi_tip_id as number <= 73));
          const featuresKult = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id as number >= 81 && x.poi_tip_id as number <= 86));
          const featuresHGSS = createPOIFeatures(resp.data.filter(x => x.geom !== undefined && x.poi_tip_id === 20));
          const featuresObilaznicaKTs = resp.data
            .filter(x => x.geom !== undefined && x.is_obilaznica_kt === true)
            .map(x => {
              // @ts-ignore
              const f = (x.geom as OlFeature<Geometry>).clone()
              f.setId(x.id as number);
              return f;
            });

          const newPoiSources = [
            featuresPlaninarski
            , featuresSkloniste
            , featuresPenjaliste
            , featuresOpasnost
            , featuresVrh
            , featuresOdmoriste
            , featuresPolazista
            , featuresPrir
            , featuresKult
            , featuresHGSS
            , featuresObilaznicaKTs
          ].map(x => new OlSourceVector<OlFeature<Geometry>>({ features: x }))

          setPoiSources(newPoiSources);

          const layerPlanKuce = createPOILayerVector("poi-planinarski", "map:layer.poi_plan_kuce_i_domovi", newPoiSources[0]);
          const layerPlanSklonista = createPOILayerVector("poi-sklonista", "map:layer.poi_plan_sklonista", newPoiSources[1]);
          const layerPenjalista = createPOILayerVector("poi-penjalista", "map:layer.poi_penjalista", newPoiSources[2]);
          // const layerOpasnost = createPOILayerVector("poi-opasnost", "map:layer.poi_opasnost", newPoiSources[3]);
          const layerVrhovi = createPOILayerVector("poi-vrh", "map:layer.vrhovi", newPoiSources[4]);
          const layerOdmorista = createPOILayerVector("poi-odmorista", "map:layer.odmorista", newPoiSources[5]);
          const layerPolazista = createPOILayerVector("poi-polazista", "map:layer.polazista", newPoiSources[6]);
          const layerPrir = createPOILayerVector("poi-prir", 'map:layer.poi_prir', newPoiSources[7]);
          const layerKult = createPOILayerVector("poi-kult", "map:layer.poi_kult", newPoiSources[8]);
          const layerHGSS = createPOILayerVector("poi-hgss", "map:layer.poi_hgss", newPoiSources[9]);
          const layerObilaznicaKTs = createPOILayerVector("obilaznica-kts", "map:layer.obilaznica_kts", newPoiSources[10]);


          const poiLayerGroup = new OlLayerGroup({
            layers: [
              layerPlanKuce
              , layerPlanSklonista
              , layerPenjalista
              , layerVrhovi
              , layerOdmorista
              , layerPolazista
              , layerPrir
              , layerKult
              , layerHGSS
              , layerObilaznicaKTs
            ]
          });
          poiLayerGroup.set('id', 'poi');
          poiLayerGroup.set('title', 'map:layer.poi');

          setClientLayersCollection(new OlCollection([poiLayerGroup]));
        } else {
          setPoiRecords([]);
        }
      }
    });
  };

  // getDefaultData() {
  //   mapService.getDefaultData().then((data) => {
  //     if (data) {
  //       const viewData = Array.isArray(data) ? Object.assign({}, data[0]) : Object.assign({}, data);
  //       this.setState((prevState) => {
  //         return {
  //           ...prevState,
  //           viewOptions: {
  //             ...prevState.viewOptions,
  //             zoom: viewData.initial_view_zoom,
  //             center: viewData.initial_view_center
  //           },
  //           zoomToExtent: viewData.default_extent
  //         };
  //       });
  //     }
  //   });
  // }

  const setViewCenter = (view: IMapView) => {
    setViewOptions((prevState) => {
      return {
        ...prevState,
        center: view.center,
        zoom: view.zoom,
      };
    });
  };

  const handlePointerMove = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
      if (highlightedPOI.current) {
        highlightedPOI.current.setStyle(poiStyle);
        highlightedPOI.current = null;
      }

      const zoom = evt.map.getView().getZoom();
      if (zoom && zoom <= 11) {
        return;
      }

      var feature = evt.map.forEachFeatureAtPixel(
        evt.pixel,
        function (feature) {
          return feature;
        },
        {
          layerFilter: (layer) => {
            const layerId = layer.get("id");
            return layerId !== null && layerId !== undefined && layerId.startsWith("poi");
          },
          hitTolerance: 8,
        }
      );

      if (feature && feature.getId() !== null && feature.getId() !== undefined) {
        const f = feature as OlFeature;
        const tip_id = f.get("poi_tip_id");
        if (tip_id != sifrarnici.poi_tipovi.opasnost) {
          highlightedPOI.current = f;
          highlightedPOI.current.setStyle(highlightedPoiStyle);
        }
      } else if (feature?.get("staza_id") && feature?.get("staza_id") === "staza") {
        setHighlightedRoutePosition(evt.coordinate);
      }
    }, [layersCollection]
  );

  const findChips = (layerKeys: string[], resp: GFIResponseType) => {
    const chips = new Array<IInfoPaneChip>; // (number, string pairs) = (id, staza_name)

    for (let ldtName of ["staze_ldt_via_adriatica", "staze_ldt_via_dinarica", "staze_ldt_cldt"]) {

      if (layerKeys.indexOf(ldtName) !== -1) {
        const ldtKey = layerKeys[layerKeys.indexOf(ldtName)];
        const ldtLayer = resp[ldtKey];
        const ldtFeature = ldtLayer[0];

        chips.push({
          'id': ldtFeature.properties.ldt_staza_id as number,
          'displayName': ldtFeature.properties.naziv_staze as string,
          'initialIdToSelect': ldtFeature.properties.id as number
        });
      }
    }

    return chips;
  }

  const handleClick = useCallback(
    (evt: OlMapBrowserEvent<any>) => {

      const zoom = evt.map.getView().getZoom();
      if (zoom && zoom < 12) {
        return;
      }

      let hit = false;

      const clickCoordinates = evt.map.getCoordinateFromPixel(evt.pixel); // epsg:3857

      const feature = evt.map.forEachFeatureAtPixel(
        evt.pixel,
        (feat) => feat,
        {
          // layerFilter: (layer) => {
          //   const layerId = layer.get("id");
          //  return layerId !== null && layerId !== undefined && layerId.startsWith("objekti");
          // }
          hitTolerance: 8,
        }
      );

      if (
        feature &&
        feature.getId() !== null &&
        feature.getId() !== undefined
      ) {
        let opasnost = false;
        selectedCoords.current = [];
        hit = true;
        const id = feature.getId() as number;
        dcPOI.GetDataSingle(id).then((resp) => {
          if (resp.data) {
            const rec = resp.data as DCRecord;
            if (rec.poi_tip_id === sifrarnici.poi_tipovi.opasnost) { opasnost = true; return; }
            showInfoPane("poi", rec.poi_tip_id as number, { type: "poi", feature: feature, record: rec, layerId: "poi", coordinates: clickCoordinates, }, null);
          }
        }).then(() => {
          if (opasnost === false) {
            clearHighlihtedRoutePosition();
            const f = feature as OlFeature<Geometry>;
            const selectedFeature = f.clone();
            const fid = f.getId();
            // selecetedFeature.setId(fid);
            selectedFeature.set('poi_id', fid);
            selectedFeature.setStyle(undefined);
            setSelectedSource(new OlSourceVector({ features: [selectedFeature] }));
          }
        });

      } else if (feature && feature.get('poi_id') !== undefined) {
        selectedCoords.current = [];
        hit = true;
        clearHighlihtedRoutePosition();
        // const id = feature.get('poi_id') as number;
        // dcPOI.GetDataSingle(id).then((resp) => {
        //   if (resp.data) {
        //     const rec = resp.data as DCRecord;
        //     showInfoPane("poi", rec.poi_tip_id as number, { type: "poi", feature: feature, record: rec, layerId: "poi", coordinates: clickCoordinates, }, null);
        //   }
        // });
        clearSelectedSource();
        setInfoPane(undefined);
      }

      if (!hit) {
        clearHighlihtedRoutePosition();
        const item = localStorage.getItem("maplayers");
        const visibility = item
          ? (JSON.parse(item) as BoolSettings)
          : ({} as BoolSettings);
        const hiddenLayers = visibility
          ? Object.keys(visibility).filter((key) => visibility[key] === false)
          : [];
        const allLayers = layersCollection
          ? flattenLayers(layersCollection.getArray()).filter(
            (x) => !(x instanceof OlLayerGroup)
          )
          : [];

        const visibleLayers = allLayers.filter((x) => hiddenLayers.indexOf(x.get("id")) === -1);
        const GSLayerNames = visibleLayers.filter((x) => x.get("query") === true).map((x) => x.get("layer"));

        gsService.getFeatureInfo(evt.map, evt.pixel, GSLayerNames).then((resp) => {

          if (resp && Object.keys(resp).length != 0) {
            selectedCoords.current = [];

            const layerKeys = Object.keys(resp);
            const poiKeys = layerKeys.filter(k => k.slice(0, 4) === "poi_");
            const lKey = layerKeys[0];
            const firstLayer = resp[lKey];
            const firstFeature = firstLayer[0];

            const type = lKey.split("_")[0];
            const subtype = lKey.split("_")[1];

            if (type === "staze" && subtype !== "ldt") {
              let chips = findChips(layerKeys, resp);
              const id = firstFeature.properties.id as number;
              dcStaze.GetDataSingle(id).then((resp) => {
                if (resp.data) {
                  const rec = resp.data as DCRecord;

                  handleShowWarningDialog(rec); // will show warning if any exist
                  showInfoPane(type, subtype, { type: type, feature: parsedFeature, record: rec, layerId: lKey, coordinates: clickCoordinates, chips: chips }, null)
                }
              });

              const parsedFeature = new OlFeature({
                geometry: new OlLineString(firstFeature.geometry.coordinates as number[])
              });

              showInfoPane(type, subtype, { type: type, feature: parsedFeature, record: firstFeature.properties, layerId: lKey, coordinates: clickCoordinates, chips: chips }, null)
              setHighlightedRoutePosition(evt.coordinate);
            } else {
              setInfoPane(undefined);
            }

            if (!(type === "staze" && subtype === "ldt")) {
              const olFeat = new OlFeature();
              switch (firstFeature.geometry.type) {
                case "Point":
                  olFeat.setGeometry(new OlPoint(firstFeature.geometry.coordinates as number[]));
                  break;
                case "LineString":
                  olFeat.setGeometry(new OlLineString(firstFeature.geometry.coordinates as number[][]))
                  olFeat.set("staza_id", "staza");
                  break;
                case "MultiLineString":
                  olFeat.setGeometry(new OlMultiLineString(firstFeature.geometry.coordinates as number[][][]));
                  break;
                default:
                  // do nothing
                  break;
              }
              setSelectedSource(new OlSourceVector({ features: [olFeat] }));
            }
            // Handle LDT Queries
            else {
              const chips = findChips(layerKeys, resp);
              clearSelectedSource();

              if (selectedCoords.current.length === 0 || (selectedCoords.current && selectedCoords.current.length == 2
                && selectedCoords.current[0].toFixed(2) !== clickCoordinates[0].toFixed(2) && selectedCoords.current[1].toFixed(2) !== clickCoordinates[1].toFixed(2))) {
                selectedCoords.current = clickCoordinates as number[];
                const newFeat = new OlFeature({ geometry: new OlPoint(clickCoordinates as number[]) });
                newFeat.set("poi_tip_id", "custom_click_point");
                showInfoPane("random", "", { type: "random", coordinates: clickCoordinates, feature: newFeat, chips: chips }, null);
                setSelectedSource(new OlSourceVector({ features: [newFeat] }));
              }
              setForceRefreshCounter((prev) => prev ? prev + 1 : 1);
            }
          } else {
            handleRandomClick(selectedCoords, clickCoordinates);
          }
        });
      }
    },
    [layersCollection]
  );

  const handleRandomClick = (selectedCoords: React.MutableRefObject<number[]>, clickCoordinates: Coordinate, chips?: Array<IInfoPaneChip>) => {
    clearSelectedSource();
    setInfoPane(undefined);

    if (selectedCoords.current.length === 0 || (selectedCoords.current && selectedCoords.current.length == 2
      && selectedCoords.current[0].toFixed(2) !== clickCoordinates[0].toFixed(2) && selectedCoords.current[1].toFixed(2) !== clickCoordinates[1].toFixed(2))) {
      selectedCoords.current = clickCoordinates as number[];
      const newFeat = new OlFeature({ geometry: new OlPoint(clickCoordinates as number[]) });
      newFeat.set("poi_tip_id", "custom_click_point");
      showInfoPane("random", "", { type: "random", coordinates: clickCoordinates, feature: newFeat, chips: chips }, null);
      setSelectedSource(new OlSourceVector({ features: [newFeat] }));
    }
    setForceRefreshCounter((prev) => prev ? prev + 1 : 1);
  }

  function showInfoPane(
    type: InfoPaneType,
    subtype: string | number,
    data: IInfoPaneData,
    ft: OlFeature<any> | null
  ) {
    setInfoPane({ type, subtype, data });
  }

  const handleViewChangeCenter = (evt: MapEvent) => {
    if (evt && evt.map) {
      const mapState = {
        center: evt.map.getView().getCenter(),
        zoom: evt.map.getView().getZoom()
      }
      // save map state to localStorage
      localStorage.setItem('public_map_state', JSON.stringify(mapState));
      // setViewOptions(prevState => {
      //   return {
      //     ...prevState,
      //     center: evt.map.getView().getCenter(),
      //     zoom: evt.map.getView().getZoom()
      //   } as ViewOptionsType
      // })
    }
  };

  const handleShowWarningDialog = (record: DCRecord) => {
    const parsedWarnings = parseRouteWarnings(record);
    const parsedWarningMSP = parsedWarnings.warningsMSP;
    const parsedWarningPodrucja = shuffleWarnings(parsedWarnings.warningsPodrucja);
    const previousWarnings = getStoredRouteWarnings();

    const areAreaWarningsIdentical = areWarningsIdentical(previousWarnings.warningsPodrucja, parsedWarningPodrucja);
    const areMSPWarningsIdentical = areWarningsIdentical(previousWarnings.warningsMSP, parsedWarningMSP);

    storeRouteWarnings(parsedWarningMSP, parsedWarningPodrucja);

    const warnings = [...parsedWarningMSP, ...parsedWarningPodrucja];
    if (warnings.length > 0 && (!areMSPWarningsIdentical || !areAreaWarningsIdentical)) {
      dialogContext.showDialog(PublicWarningDialog, {
        warnings: warnings,
        onClose: () => { dialogContext.hideDialog(); }
      })
    }
  }

  const handleShowCommentPane = (newCommentPane: any) => {
    const formatter = new WKT();
    if (newCommentPane.data.feature) {
      const wkt = formatter.writeFeature(newCommentPane.data.feature, {
        dataProjection: "EPSG:3857",
        featureProjection: "EPSG:3857",

      }
      )

      setCommentPane({ ...newCommentPane, wkt });
    } else {
      setCommentPane(newCommentPane);
    }
  };

  const handleCloseCommentPane = () => {
    setCommentPane(undefined);
  }

  const checkForShare = () => {
    const hash = new URLSearchParams(window.location.search).get("hash");
    const apiInstance = new api();

    if (!hash) return;

    apiInstance.Call(`share?hash=${hash}`, 'get').then((resp) => {
      if (!resp.success || !resp.data) return;

      const shareData = resp.data as ShareResponseData;

      const data = JSON.parse(shareData.config);
      if (data.maplayers) {
        localStorage.setItem('maplayers', data.maplayers);
      }

      if (data.route_points) {
        localStorage.setItem("route_points", data.route_points);
        const rps = Array.isArray(data.route_points) ? data.route_points : JSON.parse(data.route_points);
        loadRoutingPoints(rps);
      }

      if (data.extent?.length) {
        setZoomToExtent(data.extent);
      }

      const id = shareData.object_id ?? data.obilaznica_id;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { object_type, object_id, object_uuid } = shareData;

      if (object_type === "obilaznica" && object_id) {
        handleOpenObilaznicaPane(data.obilaznica_id, object_id);
      } else if (object_type === "ldt" && object_id) {
        handleOpenLDTPane(data.obilaznica_id, object_id);
      } else if (object_type === "izlet" && object_uuid){ 
        navigate(`/izleti/${object_uuid}`, {replace:true});
      } else if (object_type === 'staza' && id) {
        setTimeout(() => {
          dcStaze.GetDataSingle(id).then((stazeResp) => {
            if (!stazeResp.data) return;
            const rec = stazeResp.data as DCRecord;
            const featureToShow = data.position ? new OlFeature({ geometry: new OlLineString(data.position) }) : undefined;
            if (!featureToShow) return;
            featureToShow.setId(id);
            showInfoPane(
              'staze',
              rec.poi_tip_id as number,
              {
                type: 'staze',
                feature: featureToShow,
                record: rec,
                layerId: 'staze',
                coordinates: data.position ?? undefined,
              },
              null
            );
            setSelectedSource(new OlSourceVector({ features: [featureToShow] }));
          });
        }, 400);
      } else if (object_type === 'poi' && id) {
        setTimeout(() => {
          dcPOI.GetDataSingle(id).then((poiResp) => {
            if (!poiResp.data) return;
            const rec = poiResp.data as DCRecord;

            const featureToShow = data.position ? new OlFeature({ geometry: new OlPoint(data.position) }) : undefined;

            if (!featureToShow) return;

            featureToShow.setId(id);
            featureToShow.set('poi_tip_id', rec.poi_tip_id);

            showInfoPane(
              'poi',
              rec.poi_tip_id as number,
              {
                type: 'poi',
                feature: featureToShow,
                record: rec,
                layerId: 'poi',
                coordinates: data.position ?? undefined,
              },
              null
            );
          });
        }, 400);
      }
    });
  };

  const checkForComment = () => {
    const guid = new URLSearchParams(window.location.search).get("komentar");

    if (guid) {
      const apiInstance = new api();
      apiInstance.Call(`komentari/get-by-guid?guid=${guid}`, "get").then(resp => {
        if (resp.success && resp.data) {
          // @ts-ignore
          handleShowCommentPane({ data: { record: resp.data }, wkt: resp.data.geom, mode: "view", form: "default" })

          const formatter = new WKT();

          // @ts-ignore
          const feature = formatter.readFeature(resp.data.geom, {
            dataProjection: "EPSG:3857",
            featureProjection: "EPSG:3857",
          })

          setSelectedSource(new OlSourceVector({ features: [feature] }))
          const extent = feature.getGeometry()?.getExtent();

          // @ts-ignore
          setZoomToExtent(extent);
        }
      })
    }
  }

  const handleSearchShowOnMap = (id: number) => {

    const fs = poiSources?.map(x => x.getFeatures()).flat(1).filter(x => x.getId() === id);
    if (Array.isArray(fs) && fs.length > 0) {
      const f = fs[0].clone();
      f.setId(id);
      f.setStyle(undefined);
      setSelectedSource(new OlSourceVector({ features: [f] }));
    }
  }

  const handleSegmentShowOnMap = (feat: OlFeature<Geometry>) => {
    setSelectedSegmentSource(new OlSourceVector({ features: [feat] }));
  }

  const handleDrawerStage = (newStage: number) => {
    setDrawerStage(newStage);
  }

  const handlePane = (newPane: number | undefined) => {
    setPane(newPane);
  }

  const handleOpen = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen === false) {
      clearHighlihtedRoutePosition();
    }
  }

  const handleOpenObilaznicaPane = (obilaznicaId: number, poi_id: number) => {
    dcObilaznice.GetData('public/obilaznice/' + obilaznicaId).then((resp) => {
      if (resp.data) {
        setObilaznicePane({ data: { record: resp.data }, selectedPoiId: poi_id });
      }
    }).catch((err) => {

    });
  }

  const handleOpenLDTPane = (ldtId: number, ldtStageId: number) => {
    dcLDT.GetData('public/ldt/' + ldtId).then((resp) => {
      if (resp.data) {
        setLdtPane({ data: { record: resp.data }, selectedLDTId: ldtId, selectedLDTStageId: ldtStageId });
      }
    }).catch((err) => {

    });
  }

  useEffect(() => {
    if (open === false || ![3, 4].includes(pane as number)) {
      if (commentPane) { handleCloseCommentPane() }
      clearSelectedSource();
    }
  }, [open, pane])

  const selectedFeatures = selectedSource?.getFeatures();
  const hasSelectedFeature = selectedFeatures.length > 0;
  const selectedFeature = hasSelectedFeature ? selectedFeatures[0] : null;

  return userContext && layersCollection ? (
    <Map
      mapId={MAP_ID}
      height="800px"
      view={viewOptions}
      onPointermove={handlePointerMove}
      onClick={handleClick}
      onMoveend={handleViewChangeCenter}
      className="sidebar-map"
      id="public-map"
      initialized={mapInitialized}
      moveTolerance={10}
      maxTilesLoading={10}
      zoomToExtent={zoomToExtent}
    >

      <Controls>
        <RotateControl className="ol-rotate-public" autoHide tipLabel={t("map:controls.reset_north")} />
        <CoordZoomStatusControl isPublic chosenCoordinateSystem="HTRS96/TM" />
        <ZoomControl className="ol-zoom-public" zoomInClassName="ol-zoom-in-public" zoomOutClassName="ol-zoom-out-public" zoomInTipLabel={t("map:controls.zoom_in")} zoomOutTipLabel={t("map:controls.zoom_out")} />
        <Box position="absolute"
          sx={{
            bottom: {
              xs: "auto", md: "15px"
            },
            top: {
              xs: "100px", md: "auto"
            },
            right: { xs: "6px", md: "15px" },
            zIndex: 5,
            listStyle: "none"
          }}>
          {
            isMobile ? <li style={{ marginBottom: "6px" }} ref={geoLocateElementRef}></li>
              : <li style={{ marginBottom: "15px" }} ref={geoLocateElementRef}></li>
          }
          <li ref={radiusRulerElementRef}></li>
        </Box>

        {geoLocateElementRef?.current && mapInitialized ? (
          <GeoLocateControl
            id="geo-locate"
            tooltip={t("map:controls.geolocate")}
            target={geoLocateElementRef.current}
            className="custom-control-public"
            iconColor={theme.palette.mint.main}
          />
        ) : null}

        {radiusRulerElementRef?.current && mapInitialized ? (
          <RadiusRulerControl
            id="radius-ruler"
            tooltip={t("map:controls.radius_ruler")}
            target={radiusRulerElementRef.current}
            className="custom-control-public"
            iconColor={theme.palette.mint.main}
          />
        ) : null}
        <PublicNavBar handleDrawerStageInParent={handleDrawerStage} handleOpenInParent={handleOpen} handlePaneInParent={handlePane} commentsPane={commentPane} infoPane={infoPane} obilaznicePane={obilaznicePane} ldtPane={ldtPane}>
          <PublicSearchPane open={open} pane={pane} drawerStage={drawerStage} onExplore={handleSearchShowOnMap} onAddToRoute={addToRoute} handleOpenObilaznica={handleOpenObilaznicaPane} />
          <PublicRoutesPane open={open} pane={pane} drawerStage={drawerStage} handleShowCommentPane={handleShowCommentPane} />
          <PublicOptionsPane apiLayersCollection={layersCollection} clientLayersCollection={clientLayersCollection} mapId={MAP_ID} baseLayers={baseLayersCollection || []} />
          <PublicInfoPane handleAdd={addToRoute} handleShowCommentPane={handleShowCommentPane} infoPane={infoPane} handleOpenObilaznica={handleOpenObilaznicaPane} handleOpenLDTPane={handleOpenLDTPane} />
          {commentPane ?
            <PublicCommentsPane {...commentPane} pane={pane} onClose={handleCloseCommentPane} />
            : null}
          <ObilaznicePane
            obilaznicePane={obilaznicePane}
            open={open}
            pane={pane}
            drawerStage={drawerStage}
            onExplore={handleSearchShowOnMap}
            onAddToRoute={addToRoute}
            handleShowCommentPane={handleShowCommentPane}
          />
          <LDTPane
            LDTPane={ldtPane}
            open={open}
            pane={pane}
            drawerStage={drawerStage}
            onExplore={handleSegmentShowOnMap}
          />
        </PublicNavBar>
      </Controls>
      <Layers>
        {/* <Layers id="osm" osm={true} /> */}
        {/* <GeoportalBaseLayer id="osm" zIndex={1} /> */}
        <GeoAPIBaseLayers setBaseLayersCollection={setBaseLayersCollection} mapId={MAP_ID} />
        {layersCollection ? (
          <Layers>
            <GeoAPILayers layersCollection={layersCollection} />
            {/** <VectorLayer id="poi" source={poiSource} zIndex={700} style={poiStyle} /> */}
            <ClientLayer id="poi-planinarski" source={poiSources ? poiSources[0] : new OlSourceVector<OlFeature<Geometry>>} zIndex={708} style={poiStyle} />
            <ClientLayer id="poi-sklonista" source={poiSources ? poiSources[1] : new OlSourceVector<OlFeature<Geometry>>} zIndex={707} style={poiStyle} />
            <ClientLayer id="poi-penjalista" source={poiSources ? poiSources[2] : new OlSourceVector<OlFeature<Geometry>>} zIndex={706} style={poiStyle} />
            <ClientLayer id="poi-opasnost" source={poiSources ? poiSources[3] : new OlSourceVector<OlFeature<Geometry>>} zIndex={705} style={poiStyle} />
            <ClientLayer id="poi-vrh" source={poiSources ? poiSources[4] : new OlSourceVector<OlFeature<Geometry>>} zIndex={704} style={poiStyle} maxResolution={40} />
            <ClientLayer id="poi-odmorista" source={poiSources ? poiSources[5] : new OlSourceVector<OlFeature<Geometry>>} zIndex={702} style={poiStyle} />
            <ClientLayer id="poi-polazista" source={poiSources ? poiSources[6] : new OlSourceVector<OlFeature<Geometry>>} zIndex={701} style={poiStyle} />
            <ClientLayer id="poi-prir" source={poiSources ? poiSources[7] : new OlSourceVector<OlFeature<Geometry>>} zIndex={699} style={poiStyle} />
            <ClientLayer id="poi-kult" source={poiSources ? poiSources[8] : new OlSourceVector<OlFeature<Geometry>>} zIndex={698} style={poiStyle} />
            <ClientLayer id="poi-hgss" source={poiSources ? poiSources[9] : new OlSourceVector<OlFeature<Geometry>>} zIndex={700} style={poiStyle} />
            <ClientLayer id="obilaznica-kts" source={poiSources ? poiSources[10] : new OlSourceVector<OlFeature<Geometry>>} zIndex={710} style={ktStyle} />
            <VectorLayer id="selected" source={selectedSource} style={selectedStyle} zIndex={800} />
            <VectorLayer id="selected-segment" source={selectedSegmentSource} style={selectedSegmentStyle} zIndex={800} />
          </Layers>
        ) : null}
      </Layers>
      <Overlays>
        <PopupOverlay
          id="quickactions-overlay"
          className=""
          position={highlightedPOI.current ? (highlightedPOI.current.getGeometry() as OlPoint).getCoordinates() as CardPosition :
            selectedFeature ? (selectedFeature?.getGeometry() as OlPoint)?.getCoordinates() as CardPosition : undefined
          }
          autoPan={false}
          onClose={() => { }}>
          {selectedFeature ? (
            <PointQuickActions
              feature={selectedFeature}
              onAddToRoute={addToRoute}
              position={highlightedPOI.current ? (highlightedPOI.current.getGeometry() as OlPoint).getCoordinates() as CardPosition :
                selectedFeature ? (selectedFeature?.getGeometry() as OlPoint)?.getCoordinates() as CardPosition : undefined
              }
              slide={highlightedRoutePosition != null}
            />
          ) : null}
        </PopupOverlay>
        <PopupOverlay
          id="quickactions-overlay-hover"
          className=""
          position={highlightedRoutePosition ? highlightedRoutePosition as CardPosition : undefined}
          autoPan={false}
          onClose={() => { }}
          positioning="center-center">
          {highlightedRoutePosition.length ? (

            <PointQuickActions slide={false} onAddToRoute={addToRoute} coords={highlightedRoutePosition} />
          ) : null}
        </PopupOverlay>
      </Overlays>
      <Interactions>
        <DefaultInteractions />
      </Interactions>
    </Map>
  ) : null
}

export default MainMap;
